import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { AppLayout } from "@/components/layout/AppLayout";
import { lazy, Suspense } from "react";

const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const DevicesPage = lazy(() => import("@/features/devices/DevicesPage"));
const DeviceDetailPage = lazy(
  () => import("@/features/devices/DeviceDetailPage"),
);
const AnalyticsPage = lazy(() => import("@/features/analytics/AnalyticsPage"));
const AlertsPage = lazy(() => import("@/features/alerts/AlertsPage"));
const AdminPage = lazy(() => import("@/features/admin/AdminPage"));
const HelpPage = lazy(() => import("@/features/help/HelpPage"));
const TrackingPage = lazy(
  () => import("@/features/tracking/TrackingPage"),
);
const ProfilePage = lazy(
  () => import("@/features/profile/ProfilePage"),
);

function RequireAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
}

function RequireGuest() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Outlet />;
}

function PageFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route element={<RequireGuest />}>
            <Route path="/login" element={<LoginPage />} />
          </Route>

          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/devices" element={<DevicesPage />} />
              <Route path="/devices/:deviceId" element={<DeviceDetailPage />} />
              <Route
                path="/analytics"
                element={<Navigate to="/analytics/fleet" replace />}
              />
              <Route
                path="/analytics/:section"
                element={<AnalyticsPage />}
              />
              <Route
                path="/reports"
                element={
                  <Navigate to="/reports/device-inventory" replace />
                }
              />
              <Route
                path="/reports/:section"
                element={<AnalyticsPage />}
              />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/tracking" element={<TrackingPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/help" element={<HelpPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
